import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../auth/context/AuthContext';
import { AiFillCheckCircle } from 'react-icons/ai';
import { cn } from '../cn';
import PricingHeader from '../components/landing-page/PricingHeader';

interface PricingPlan {
  id: string;
  name: string;
  price: string;
  description: string;
  features: string[];
  popular?: boolean;
  cta: string;
}

const pricingPlans: PricingPlan[] = [
  {
    id: 'basic',
    name: 'Basic',
    price: '$29',
    description: 'Perfect for getting started',
    features: [
      '5 pricing tables',
      'Basic analytics',
      'Email support',
      'Standard variations'
    ],
    cta: 'Get Started'
  },
  {
    id: 'pro',
    name: 'Pro',
    price: '$99',
    description: 'Our most popular plan',
    features: [
      'Unlimited pricing tables',
      'Advanced analytics',
      'Priority support',
      'Advanced variations',
      'Custom integrations',
      'A/B testing insights'
    ],
    popular: true,
    cta: 'Get Started'
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    price: '$299',
    description: 'For large teams and organizations',
    features: [
      'Everything in Pro',
      'Custom integrations',
      'Dedicated support',
      'White-label options',
      'API access',
      'Custom onboarding',
      'SLA guarantee'
    ],
    cta: 'Get Started'
  }
];

const bestDealPlanId = 'pro';

export default function PricingPage() {
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState<string | null>(null);

  const handlePlanClick = async (planId: string) => {
    if (!user) {
      // Redirect to login if not authenticated
      window.location.href = '/login';
      return;
    }

    setIsLoading(planId);
    
    try {
      // Redirect to subscription page
      window.location.href = '/subscription';
    } catch (error) {
      console.error('Error handling plan selection:', error);
    } finally {
      setIsLoading(null);
    }
  };

  return (
    <div className="bg-white dark:bg-boxdark-2">
      <PricingHeader />
      <div className="py-10 lg:mt-10">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div id="pricing" className="mx-auto max-w-4xl text-center">
          <h2 className="mt-2 text-4xl font-bold tracking-tight text-gray-900 sm:text-5xl dark:text-white">
            Pick your <span className="text-yellow-500">pricing</span>
          </h2>
        </div>
        <p className="mx-auto mt-6 max-w-2xl text-center text-lg leading-8 text-gray-600 dark:text-white">
          Choose the plan that fits your pricing optimization goals and start testing faster.
        </p>
        
        <div className="isolate mx-auto mt-16 grid max-w-md grid-cols-1 gap-y-8 gap-x-8 sm:mt-20 lg:mx-0 lg:max-w-none lg:grid-cols-3">
          {pricingPlans.map((plan) => (
            <div
              key={plan.id}
              className={cn(
                'relative flex flex-col grow justify-between rounded-3xl ring-gray-900/10 dark:ring-gray-100/10 overflow-hidden p-8 xl:p-10',
                {
                  'ring-2': plan.id === bestDealPlanId,
                  'ring-1 lg:mt-8': plan.id !== bestDealPlanId,
                }
              )}
            >
              {plan.popular && (
                <div
                  className="absolute top-0 right-0 -z-10 w-full h-full transform-gpu blur-3xl"
                  aria-hidden="true"
                >
                  <div
                    className="absolute w-full h-full bg-gradient-to-br from-amber-400 to-purple-300 opacity-30 dark:opacity-50"
                    style={{
                      clipPath: 'circle(670% at 50% 50%)',
                    }}
                  />
                </div>
              )}
              <div className="mb-8">
                <div className="flex items-center justify-between gap-x-4">
                  <h3 id={plan.id} className="text-gray-900 text-lg font-semibold leading-8 dark:text-white">
                    {plan.name}
                  </h3>
                  {plan.popular && (
                    <span className="inline-flex items-center rounded-full bg-yellow-400/10 px-2.5 py-1 text-xs font-medium text-yellow-400">
                      Most Popular
                    </span>
                  )}
                </div>
                <p className="mt-4 text-sm leading-6 text-gray-600 dark:text-white">
                  {plan.description}
                </p>
                <p className="mt-6 flex items-baseline gap-x-1 dark:text-white">
                  <span className="text-4xl font-bold tracking-tight text-gray-900 dark:text-white">
                    {plan.price}
                  </span>
                  <span className="text-sm font-semibold leading-6 text-gray-600 dark:text-white">
                    /month
                  </span>
                </p>
                <ul role="list" className="mt-8 space-y-3 text-sm leading-6 text-gray-600 dark:text-white">
                  {plan.features.map((feature) => (
                    <li key={feature} className="flex gap-x-3">
                      <AiFillCheckCircle className="h-6 w-5 flex-none text-yellow-500" aria-hidden="true" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
              <button
                onClick={() => handlePlanClick(plan.id)}
                aria-describedby={plan.id}
                className={cn(
                  {
                    'bg-yellow-500 text-white hover:text-white shadow-sm hover:bg-yellow-400':
                      plan.id === bestDealPlanId,
                    'text-gray-600 ring-1 ring-inset ring-purple-200 hover:ring-purple-400':
                      plan.id !== bestDealPlanId,
                  },
                  {
                    'opacity-50 cursor-wait': isLoading === plan.id,
                  },
                  'mt-8 block rounded-md py-2 px-3 text-center text-sm dark:text-white font-semibold leading-6 focus-visible:outline focus-visible:outline-2 focus-visible:outline-yellow-400'
                )}
                disabled={isLoading === plan.id}
              >
                {isLoading === plan.id ? 'Loading...' : (!!user ? plan.cta : 'Log in to get started')}
              </button>
            </div>
          ))}
        </div>
        
        <div className="mt-16 text-center">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Need a custom setup? <Link to="/support" className="text-yellow-500 hover:text-yellow-400">Reach support</Link> and we can help.
          </p>
        </div>
        </div>
      </div>
    </div>
  );
}
